import type { PlayerOnlineStatus } from '@/api/memberPortal/playerOnlineStatusType'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  requestGetMyPlayerOnlineStatus,
  requestPutMyPlayerOnlineStatus,
} from '@/api/memberPortal/playerOnlineStatus'
import { EnumIdType } from '@/typings'
import { useMemberModulesStore } from './memberModules'
import { useUserStore } from './user'

export const PLAYER_ONLINE_STATUS_LABELS: Record<PlayerOnlineStatus, string> = {
  0: '空闲',
  1: '离线',
  2: '忙碌',
}

export const usePlayerOnlineStatusStore = defineStore(
  'playerOnlineStatus',
  () => {
    const userStore = useUserStore()
    const memberModulesStore = useMemberModulesStore()

    // 默认按离线兜底，避免接口未返回或退出登录后短暂展示旧在线状态。
    const onlineStatus = ref<PlayerOnlineStatus>('1')
    const onlineStatusDesc = ref<string>(PLAYER_ONLINE_STATUS_LABELS[onlineStatus.value])
    const loading = ref(false)
    const updating = ref(false)

    // 只有当前身份是陪玩，且当前俱乐部开启会员端能力时，页面才展示在线状态入口。
    const shouldShowStatus = computed(() => {
      return userStore.userInfo.currentRole.roleCode === EnumIdType.ROLE_CLUB_PLAYER
        && memberModulesStore.isMemberModuleEnabled
    })

    // 查询和修改接口依赖当前登录态中的俱乐部与陪玩身份，上下文不完整时不发请求。
    const canRequestStatus = computed(() => {
      return shouldShowStatus.value && !!userStore.userInfo.clubId
    })

    const isOnline = computed(() => onlineStatus.value !== '1')
    const statusText = computed(() => onlineStatusDesc.value || PLAYER_ONLINE_STATUS_LABELS[onlineStatus.value])

    // 清空时统一回到离线态，适用于退出登录、身份/俱乐部不满足展示条件等场景。
    function resetStatus() {
      onlineStatus.value = '1'
      onlineStatusDesc.value = PLAYER_ONLINE_STATUS_LABELS[onlineStatus.value]
      loading.value = false
      updating.value = false
    }

    // 按当前身份和会员端开关判断是否需要查询，避免非陪玩或无会员端时打无效接口。
    async function fetchStatusIfNeeded() {
      if (!canRequestStatus.value) {
        resetStatus()
        return
      }

      // onShow、切换身份、切换俱乐部可能连续触发，这里收口重复查询。
      if (loading.value) {
        return
      }

      loading.value = true
      try {
        const { data } = await requestGetMyPlayerOnlineStatus()
        // 后端未返回状态时按离线处理，避免展示未知状态。
        onlineStatus.value = data.onlineStatus || '1'
        onlineStatusDesc.value = data.onlineStatusDesc || PLAYER_ONLINE_STATUS_LABELS[onlineStatus.value]
      }
      finally {
        loading.value = false
      }
    }

    // 手动切换在线状态，成功后以后端返回值作为最终展示状态。
    async function updateStatus(status: PlayerOnlineStatus) {
      if (!canRequestStatus.value || updating.value) {
        return
      }

      updating.value = true
      try {
        const { data } = await requestPutMyPlayerOnlineStatus({ onlineStatus: status })
        // 修改接口异常返回空状态时同样兜底离线。
        onlineStatus.value = data.onlineStatus || '1'
        onlineStatusDesc.value = data.onlineStatusDesc || PLAYER_ONLINE_STATUS_LABELS[onlineStatus.value]
      }
      finally {
        updating.value = false
      }
    }

    return {
      onlineStatus,
      onlineStatusDesc,
      loading,
      updating,
      shouldShowStatus,
      canRequestStatus,
      isOnline,
      statusText,
      fetchStatusIfNeeded,
      updateStatus,
      resetStatus,
    }
  },
  {
    persist: true,
  },
)
